import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api';
import Icon from './Icon';

// Fireflies kaydı — Fireflies arayüzü gibi: solda video, sağda Özet / Yapılacaklar.
// Video linki süreli; her açılışta backend'den taze alınır.

export const fmtTs = (s) => {
  if (s == null) return '';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  const pad = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

// Fireflies özetindeki **kalın** işaretlerini metin olarak (HTML'siz) kalına çevir
export const Rich = ({ text }) => String(text || '').split(/\*\*(.+?)\*\*/g)
  .map((part, i) => (i % 2 ? <strong key={i} style={{ color: 'var(--text)' }}>{part}</strong> : part));

// "- **Konu:** metin" satırlarını başlıklı bloklara ayır; kalıba uymayan satırlar düz paragraf olur
export function parseSummary(text) {
  return String(text || '').split('\n').map(l => l.trim()).filter(Boolean).map(l => {
    const m = l.replace(/^[-•*]\s*/, '').match(/^\*\*(.+?):?\*\*:?\s*(.*)$/);
    return m ? { topic: m[1].replace(/:$/, ''), text: m[2] } : { topic: null, text: l.replace(/^[-•*]\s*/, '') };
  });
}

// Fireflies metnindeki emojileri gösterme
const stripEmoji = (t) => String(t || '').replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu, '').trim();
const toSec = (t) => {
  const p = String(t).split(':').map(Number);
  return p.length === 3 ? p[0] * 3600 + p[1] * 60 + p[2] : p[0] * 60 + p[1];
};

// Fireflies "notlar" (shorthand_bullet): "**Başlık** (00:34 - 07:00)" + altında maddeler
export function parseNotes(text) {
  const chapters = [];
  for (const raw of String(text || '').split('\n')) {
    const line = stripEmoji(raw);
    if (!line) continue;
    const h = line.match(/^\*\*(.+?)\*\*\s*\((\d{1,2}:\d{2}(?::\d{2})?)\s*[-–]\s*(\d{1,2}:\d{2}(?::\d{2})?)\)/);
    if (h) { chapters.push({ title: h[1].trim(), start: toSec(h[2]), end: toSec(h[3]), bullets: [] }); continue; }
    const b = line.replace(/^[-•*]\s*/, '');
    if (chapters.length) chapters[chapters.length - 1].bullets.push(b);
  }
  return chapters;
}

// Maddenin konuşulduğu bölüm: zamanı içeren en dar bölüm, yoksa ondan önce başlayan son bölüm
export function chapterFor(chapters, ts) {
  if (ts == null || !chapters.length) return null;
  const inside = chapters.filter(c => ts >= c.start && ts <= c.end).sort((a, b) => (a.end - a.start) - (b.end - b.start));
  if (inside.length) return inside[0];
  const before = chapters.filter(c => c.start <= ts).sort((a, b) => b.start - a.start);
  return before[0] || null;
}

const STATUS = {
  open: { label: 'Bekliyor', cls: 'mv-chip--open' },
  accepted: { label: 'Görev oldu', cls: 'mv-chip--done' },
  dismissed: { label: 'Yoksayıldı', cls: 'mv-chip--muted' },
};

// Maddeyi pencere içinden göreve çevirme formu (Görevler'deki formun kısa hali)
function QuickTask({ item, meeting, users, chapter, onDone, onCancel }) {
  const [f, setF] = useState({
    title: String(item.text || '').slice(0, 200),
    assignee: item.assignee_user_id ? String(item.assignee_user_id) : '',
    due_date: '',
    priority: 'medium',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!f.title.trim()) { setErr('Başlık boş olamaz'); return; }
    setSaving(true); setErr('');
    try {
      const when = meeting.meeting_date ? new Date(meeting.meeting_date).toLocaleDateString('tr-TR') : '';
      const r = await api.post('/tasks', {
        title: f.title.trim(),
        company: item.brand_name || meeting.account_company || '',
        assignee_ids: f.assignee ? [Number(f.assignee)] : [],
        due_date: f.due_date || null,
        priority: f.priority,
        status: 'todo',
        related_to: meeting.account_id ? 'account' : 'internal',
        related_id: meeting.account_id || null,
        description: [
          `Toplantı: ${meeting.title}${when ? ` (${when})` : ''}`,
          item.ts_seconds != null ? `Kayıttaki an: ${fmtTs(item.ts_seconds)}` : null,
          item.assignee_name ? `Toplantıda atanan: ${item.assignee_name}` : null,
          chapter ? `\nKonu (Fireflies): ${chapter.title} (${fmtTs(chapter.start)}–${fmtTs(chapter.end)})` : null,
          ...(chapter ? chapter.bullets.slice(0, 8).map(b => `- ${b}`) : []),
        ].filter(Boolean).join('\n'),
        meeting_item_id: item.id,
      });
      onDone({ status: 'accepted', task_id: r.data?.id });
    } catch (e) {
      const msg = e.response?.data?.error || 'Görev kaydedilemedi';
      if (e.response?.status === 409) onDone({ status: 'accepted' }, msg);
      else { setErr(msg); setSaving(false); }
    }
  };

  return (
    <div className="mv__quick" onClick={(e) => e.stopPropagation()}>
      <input className="form-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Görev başlığı" />
      <div className="mv__quick-row">
        <select className="form-input" value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })} title="Atanan">
          <option value="">Atanmamış</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <input className="form-input" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} title="Bitiş tarihi" />
        <select className="form-input" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} title="Öncelik">
          <option value="low">Düşük</option>
          <option value="medium">Orta</option>
          <option value="high">Yüksek</option>
        </select>
      </div>
      {err && <div style={{ color: 'var(--red)', fontSize: 12 }}>{err}</div>}
      <div className="mv__quick-row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>Vazgeç</button>
        <button type="button" className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Görevi oluştur'}</button>
      </div>
    </div>
  );
}

export default function MeetingVideoModal({ meeting, startAt, onClose, onChanged }) {
  const videoRef = useRef(null);
  const [media, setMedia] = useState(null);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(startAt != null ? 'todo' : 'summary');
  const [now, setNow] = useState(0);
  const [users, setUsers] = useState([]);
  const [openForm, setOpenForm] = useState(null);
  const [openCtx, setOpenCtx] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/meetings/${meeting.id}/fireflies`)
      .then(r => { if (alive) setMedia(r.data); })
      .catch(e => { if (alive) setError(e.response?.data?.error || 'Kayıt alınamadı'); });
    api.get('/meetings/action-items', { params: { meeting_id: meeting.id } })
      .then(r => { if (alive) setItems(r.data); })
      .catch(() => {});
    api.get('/users')
      .then(r => { if (alive) setUsers((r.data || []).filter(u => u.is_active !== false)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [meeting.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const updateItem = (id, patch) => setItems(list => list.map(i => (i.id === id ? { ...i, ...patch } : i)));

  const setStatus = async (item, action) => {
    try {
      await api.post(`/meetings/action-items/${item.id}/${action}`);
      updateItem(item.id, { status: action === 'dismiss' ? 'dismissed' : 'open' });
      if (onChanged) onChanged();
    } catch (e) {
      setNotice(e.response?.data?.error || 'İşlem yapılamadı');
    }
  };

  // Video henüz yüklenmediyse istenen anı sakla, hazır olunca oraya git
  const pendingSeek = useRef(startAt != null ? startAt : null);
  const seek = (s) => {
    if (s == null) return;
    const v = videoRef.current;
    if (!v || v.readyState < 1) { pendingSeek.current = s; return; }
    v.currentTime = s;
    v.play().catch(() => {});
  };
  const onReady = () => {
    if (pendingSeek.current != null) { const t = pendingSeek.current; pendingSeek.current = null; seek(t); }
  };

  const sections = useMemo(() => parseSummary(meeting.summary || meeting.notes), [meeting.summary, meeting.notes]);
  const chapters = useMemo(() => parseNotes(media?.notes), [media]);
  // Tıklanan bölüm, video onun aralığındayken vurgulu kalsın (Fireflies bölümleri zaman olarak binebiliyor)
  const [picked, setPicked] = useState(null);
  const nowChapter = useMemo(() => (
    picked && now >= picked.start && now <= picked.end ? picked : chapterFor(chapters, now)
  ), [chapters, now, picked]);

  // Yapılacaklar kişiye göre gruplu (Fireflies'taki gibi)
  const groups = useMemo(() => {
    const map = new Map();
    for (const i of items) {
      const who = i.assignee_user_name || i.assignee_name || 'Atanmamış';
      if (!map.has(who)) map.set(who, { who, inCrm: !!i.assignee_user_id, list: [] });
      map.get(who).list.push(i);
    }
    return [...map.values()];
  }, [items]);

  // Videoda şu an konuşulan madde: zamanı geçmiş en son madde
  const currentId = useMemo(() => {
    let best = null;
    for (const i of items) if (i.ts_seconds != null && i.ts_seconds <= now && (!best || i.ts_seconds > best.ts_seconds)) best = i;
    return best && now - best.ts_seconds < 120 ? best.id : null;
  }, [items, now]);

  const src = media?.video_url || media?.audio_url;
  const people = Array.isArray(meeting.participant_emails) ? meeting.participant_emails.length : null;
  const brand = meeting.account_company || meeting.lead_company;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="mv" onClick={(e) => e.stopPropagation()}>
        <div className="mv__head">
          <div style={{ minWidth: 0 }}>
            <h2 className="mv__title">{meeting.title}</h2>
            <div className="mv__meta">
              {brand && <span className="mv-chip">{brand}</span>}
              {meeting.meeting_date && (
                <span><Icon name="calendar" size={13} /> {new Date(meeting.meeting_date).toLocaleString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              )}
              {meeting.duration_minutes ? <span><Icon name="clock" size={13} /> {meeting.duration_minutes} dk</span> : null}
              {people ? <span title={meeting.attendees}><Icon name="users" size={13} /> {people} katılımcı</span> : null}
              {meeting.transcript_url && (
                <a href={meeting.transcript_url} target="_blank" rel="noreferrer" className="mv__link">Fireflies'ta aç ↗</a>
              )}
            </div>
          </div>
          <button type="button" className="mv__close" onClick={onClose} aria-label="Kapat">×</button>
        </div>

        <div className="mv__body">
          <div className="mv__left">
            <div className="mv__video">
              {error ? (
                <div className="mv__placeholder">{error}</div>
              ) : !src ? (
                <div className="mv__placeholder">Kayıt yükleniyor…</div>
              ) : media.video_url ? (
                <video ref={videoRef} src={src} controls preload="metadata"
                  onLoadedMetadata={onReady}
                  onTimeUpdate={(e) => { const t = Math.floor(e.currentTarget.currentTime); if (t !== now) setNow(t); }} />
              ) : (
                <audio ref={videoRef} src={src} controls onLoadedMetadata={onReady}
                  onTimeUpdate={(e) => setNow(Math.floor(e.currentTarget.currentTime))} style={{ width: '100%', margin: 16 }} />
              )}
            </div>
            {meeting.attendees && (
              <div className="mv__people">
                <div className="mv__label">Katılımcılar</div>
                <div className="mv__people-list">
                  {String(meeting.attendees).split(',').map(s => s.trim()).filter(Boolean).map(e => (
                    <span key={e} className="mv-chip mv-chip--muted">{e}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mv__right">
            <div className="mv__tabs">
              <button type="button" className={`mv__tab ${tab === 'summary' ? 'mv__tab--on' : ''}`} onClick={() => setTab('summary')}>Notlar</button>
              <button type="button" className={`mv__tab ${tab === 'todo' ? 'mv__tab--on' : ''}`} onClick={() => setTab('todo')}>
                Yapılacaklar <span className="mv__count">{items.length}</span>
              </button>
            </div>

            <div className="mv__panel">
              {notice && (
                <div className="mv__notice" onClick={() => setNotice('')}>{notice}</div>
              )}
              {tab === 'summary' && (
                <div className="mv__summary">
                  {media?.gist && <div className="mv__gist">{stripEmoji(media.gist)}</div>}
                  {media?.keywords?.length > 0 && (
                    <div className="mv__keywords">{media.keywords.map(k => <span key={k}>{k}</span>)}</div>
                  )}
                  {chapters.length > 0 ? chapters.map((c, i) => (
                    <div key={i} className={`mv__chapter ${nowChapter === c ? 'mv__chapter--now' : ''}`}
                      onClick={() => { setPicked(c); seek(c.start); }} title="Videoda bu bölüme git">
                      <div className="mv__chapter-head">
                        <span className="mv__ts">{fmtTs(c.start)}–{fmtTs(c.end)}</span>
                        <span className="mv__topic">{c.title}</span>
                        <span className="mv__chapter-play"><Icon name="play" size={11} /> {nowChapter === c ? 'Oynatılıyor' : 'İzle'}</span>
                      </div>
                      <ul className="mv__bullets">{c.bullets.map((b, j) => <li key={j}><Rich text={b} /></li>)}</ul>
                    </div>
                  )) : !media ? (
                    <div className="text-muted">Notlar yükleniyor…</div>
                  ) : sections.length > 0 ? sections.map((sec, i) => (
                    <div key={i} className="mv__section">
                      {sec.topic && <div className="mv__topic">{sec.topic}</div>}
                      <div className="mv__text"><Rich text={sec.text} /></div>
                    </div>
                  )) : <div className="text-muted">Bu toplantı için not yok.</div>}
                </div>
              )}

              {tab === 'todo' && (
                groups.length === 0 ? <div className="text-muted">Bu toplantıda aksiyon maddesi yok.</div> : groups.map(g => (
                  <div key={g.who} className="mv__group">
                    <div className="mv__who">
                      <span className="mv__avatar">{g.who.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</span>
                      {g.who}
                      {!g.inCrm && g.who !== 'Atanmamış' && <span className="text-muted" style={{ fontWeight: 400, fontSize: 12 }}>· CRM kullanıcısı değil</span>}
                      <span className="text-muted" style={{ marginLeft: 'auto', fontWeight: 400, fontSize: 12 }}>{g.list.length}</span>
                    </div>
                    {g.list.map(i => (
                      <div key={i.id} className={`mv__item-wrap ${currentId === i.id ? 'mv__item--now' : ''}`}>
                        <div className="mv__item">
                          <button type="button" className="mv__ts" onClick={() => seek(i.ts_seconds)} disabled={i.ts_seconds == null}
                            title="Videoda bu ana git">{i.ts_seconds != null ? fmtTs(i.ts_seconds) : '—'}</button>
                          <span className="mv__item-text">
                            {i.text}
                            {(() => {
                              const c = chapterFor(chapters, i.ts_seconds);
                              return c && (
                                <span className="mv__ctx-row">
                                  <button type="button" className="mv__ctx mv__ctx--time" onClick={() => seek(c.start)} title="Videoda bu bölüme git">
                                    {fmtTs(c.start)}–{fmtTs(c.end)}
                                  </button>
                                  <button type="button" className="mv__ctx" onClick={() => setOpenCtx(openCtx === i.id ? null : i.id)}>
                                    {c.title} {openCtx === i.id ? '▴' : '▾'}
                                  </button>
                                </span>
                              );
                            })()}
                          </span>
                          {i.status === 'open' && openForm !== i.id ? (
                            <span className="mv__actions">
                              <button type="button" className="mv__act mv__act--add" onClick={() => setOpenForm(i.id)}>
                                <Icon name="plus" size={12} /> Göreve ekle
                              </button>
                              <button type="button" className="mv__act" onClick={() => setStatus(i, 'dismiss')} title="Bu maddeyi görev yapma">Yoksay</button>
                            </span>
                          ) : i.status === 'dismissed' ? (
                            <span className="mv__actions">
                              <span className={`mv-chip ${STATUS.dismissed.cls}`}>{STATUS.dismissed.label}</span>
                              <button type="button" className="mv__act" onClick={() => setStatus(i, 'restore')}>Geri al</button>
                            </span>
                          ) : i.status === 'accepted' ? (
                            <span className={`mv-chip ${STATUS.accepted.cls}`}>{STATUS.accepted.label}</span>
                          ) : null}
                        </div>
                        {openCtx === i.id && chapterFor(chapters, i.ts_seconds) && (
                          <ul className="mv__bullets mv__bullets--ctx">
                            {chapterFor(chapters, i.ts_seconds).bullets.map((b, j) => <li key={j}><Rich text={b} /></li>)}
                          </ul>
                        )}
                        {openForm === i.id && (
                          <QuickTask item={i} meeting={meeting} users={users} chapter={chapterFor(chapters, i.ts_seconds)}
                            onCancel={() => setOpenForm(null)}
                            onDone={(patch, msg) => {
                              updateItem(i.id, patch);
                              setOpenForm(null);
                              setNotice(msg || 'Görev oluşturuldu — Görevler sayfasında görünür.');
                              if (onChanged) onChanged();
                            }} />
                        )}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
